import { useEffect, useRef } from 'react';

// The configurator sub pages (BOM, Specs) now open at the top rather than
// inheriting the form's offset. That would otherwise cost the user their place
// in the form on the way back, since the inherited offset was also what carried
// them back to it - so the offset is remembered explicitly instead.
//
// Module level rather than sessionStorage on purpose: the offset only means
// anything while redux still holds the answers that made the form that tall,
// and that state dies with the tab too.
interface IStoredScroll {
  modelId: number | null;
  offset: number;
}

let storedScroll: IStoredScroll | null = null;

// Storing the offset is not enough to earn a restore: leaving a sub page by the
// header logo or Orders and later rebuilding the same model from scratch would
// otherwise drop the user halfway down a form they have not filled in yet. Only
// the Back navigation out of a sub page arms it, so a restore means the user
// really did come straight back from BOM or Specs.
let isRestoreArmed = false;

// Web fonts, images and the price column can still shift the form's height for
// a few frames after it renders, so the restore retries until the document is
// tall enough to hold the offset.
const MAX_RESTORE_FRAMES = 60;

/**
 * Called on the way into a sub page, while the window is still showing the
 * configurator.
 */
export const rememberConfiguratorScroll = (modelId: number | null) => {
  storedScroll = { modelId, offset: window.scrollY };
  isRestoreArmed = false;
};

/**
 * Called by the sub page's Back navigation, on the way to the configurator.
 * Any other way out of a sub page leaves the offset unarmed, so the next
 * configurator mount discards it.
 */
export const armConfiguratorScrollRestore = () => {
  if (storedScroll) {
    isRestoreArmed = true;
  }
};

/**
 * Whether a restore is actually going to happen. Lets the configurator's other
 * scroll effects stand aside rather than fight it.
 */
export const hasStoredConfiguratorScroll = () =>
  storedScroll !== null && isRestoreArmed;

/**
 * Puts the configurator back where the user left it before opening BOM or
 * Specs. `isReady` should flip once the questions are in the DOM - restoring
 * against a form that has not rendered yet would just clamp the offset to an
 * empty document.
 *
 * The offset is consumed by the first mount that can use it, so a later visit
 * to the same model starts wherever it normally would. A mount that was not
 * reached by the sub page's Back navigation throws the offset away instead.
 */
export const useRestoreConfiguratorScroll = (
  modelId: number | null,
  isReady: boolean
) => {
  const hasSettledRef = useRef<boolean>(false);

  useEffect(() => {
    if (hasSettledRef.current || !storedScroll) {
      return;
    }
    // Reached from anywhere but a sub page's Back, or a different model than
    // the one that stored it: either way the offset points at nothing the user
    // is looking at, so drop it rather than carry it into a later visit.
    if (!isRestoreArmed || storedScroll.modelId !== modelId) {
      storedScroll = null;
      isRestoreArmed = false;
      hasSettledRef.current = true;
      return;
    }
    if (!isReady) {
      return;
    }

    const target = storedScroll.offset;
    let frames = 0;
    let frameId: number;
    const restore = () => {
      if (hasSettledRef.current) {
        return;
      }
      const maxScroll = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        0
      );
      if (maxScroll >= target || frames >= MAX_RESTORE_FRAMES) {
        window.scrollTo(0, Math.min(target, maxScroll));
        storedScroll = null;
        isRestoreArmed = false;
        hasSettledRef.current = true;
        return;
      }
      frames++;
      frameId = requestAnimationFrame(restore);
    };
    frameId = requestAnimationFrame(restore);

    return () => cancelAnimationFrame(frameId);
  }, [modelId, isReady]);
};
